import React, {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { isNil } from 'lodash';
import { toast } from 'react-toastify';

export interface ICopiedDisbursementData {
  // Common fields
  name?: string;

  // Check-specific fields
  mailingAddress?: {
    streetAddress?: string;
    unit?: string | null;
    city?: string;
    stateAbbreviation?: string;
    zipCode?: string;
  };

  // Wire-specific fields
  wireDetails?: {
    beneficiary?: string;
    routingNumber?: string;
    accountNumber?: string;
    bankName?: string;
  };
}

interface DisbursementCopyContextState {
  copiedData: ICopiedDisbursementData | null;
  setCopiedData: Dispatch<SetStateAction<ICopiedDisbursementData | null>>;
}

const initialCopyState: ICopiedDisbursementData | null = null;

const DisbursementCopyContext = createContext<DisbursementCopyContextState | null>(null);

export const DisbursementCopyContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [copiedData, setCopiedData] = useState<ICopiedDisbursementData | null>(() => initialCopyState);

  const memoizedValue = useMemo(() => {
    return { copiedData, setCopiedData };
  }, [copiedData]);

  return <DisbursementCopyContext.Provider value={memoizedValue}>{children}</DisbursementCopyContext.Provider>;
};

export const useDisbursementCopyContext = () => {
  const context = useContext(DisbursementCopyContext);
  if (isNil(context)) {
    throw new Error('useDisbursementCopyContext must be used within a DisbursementCopyContextProvider');
  }

  const { copiedData, setCopiedData } = context;

  const copyDisbursementData = useCallback(
    (data: ICopiedDisbursementData) => {
      setCopiedData(data);
      toast(`Beneficiary ${data.name} info, has been copied to the clipboard`);
    },
    [setCopiedData]
  );

  const clearCopiedData = useCallback(() => {
    setCopiedData(null);
  }, [setCopiedData]);

  const hasCopiedData = useMemo(() => {
    return !isNil(copiedData);
  }, [copiedData]);

  return {
    copiedData,
    setCopiedData,
    copyDisbursementData,
    clearCopiedData,
    hasCopiedData,
  };
};
